import { useAppForm } from '@massilia/features'
import { toError, useUpdateMyPasswordMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useState } from 'react'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { updatePasswordSchema } from '@/lib/schemas/personal-informations/personal-informations.schemas'

import { EditableSection } from './editable-section'

export function PasswordSection() {
  const [isEditing, setIsEditing] = useState(false)
  const { mutateAsync: updatePassword } = useUpdateMyPasswordMutation()

  const form = useAppForm({
    defaultValues: {
      confirmNewPassword: '',
      currentPassword: '',
      newPassword: '',
    },
    onSubmit: async ({ value }) => {
      try {
        await updatePassword(value)
        setIsEditing(false)
      } catch (error) {
        toast.error(resolveAuthErrorMessage(toError(error)))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          updatePasswordSchema
        ),
    },
  })

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  return (
    <EditableSection
      actionLabel="Modifier le mot de passe"
      isEditing={isEditing}
      onToggleEdit={() => (isEditing ? handleCancel() : setIsEditing(true))}
      editForm={
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <form.AppField
            name="currentPassword"
            children={(field) => <field.PasswordField label="Mot de passe actuel" />}
          />
          <form.AppField
            name="newPassword"
            children={(field) => <field.PasswordField label="Nouveau mot de passe" />}
          />
          <form.AppField
            name="confirmNewPassword"
            children={(field) => <field.PasswordField label="Confirmer le nouveau mot de passe" />}
          />
          <div className="flex gap-3">
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <>
                  <Button
                    className="bg-brand-600 text-white hover:bg-brand-700"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={handleCancel}
                    type="button"
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </>
              )}
            </form.Subscribe>
          </div>
        </form>
      }
    >
      <div className="flex gap-6 py-2">
        <span className="w-48 shrink-0 text-sm text-gray-light-700">Mot de passe</span>
        <span className="text-sm text-gray-light-900">••••••••</span>
      </div>
    </EditableSection>
  )
}
